// ============================================================================
// FeedCostsContext — Phase 2.0.6
// ============================================================================
// Thin Provider for feed-cost + missed-report clearing state.
//
//   feedCosts     : { starter, grower, layer, pig, grit } — $/lb
//   missedCleared : Set — cleared missed-report keys
// ============================================================================
import {createContext, useContext, useState} from 'react';
import type {ReactNode} from 'react';

// Value type INFERRED from the builder below, and here inference does the whole
// job with no annotations at all: the feed-cost initializer is a real literal,
// so its five $/lb rates are already known to be numbers, and `new Set()`
// already says `Set<unknown>` — the missed-report keys are composed by the home
// dashboard, not by this module, so `unknown` is what it actually knows.
function useFeedCostsValue() {
  const [feedCosts, setFeedCosts] = useState({
    starter: 0,
    grower: 0,
    layer: 0,
    pig: 0,
    grit: 0,
  });
  const [missedCleared, setMissedCleared] = useState(new Set());

  return {
    feedCosts,
    setFeedCosts,
    missedCleared,
    setMissedCleared,
  };
}

type FeedCostsContextValue = ReturnType<typeof useFeedCostsValue>;

const FeedCostsContext = createContext<FeedCostsContextValue | null>(null);

export function FeedCostsProvider({children}: {children?: ReactNode}) {
  const value = useFeedCostsValue();
  return <FeedCostsContext.Provider value={value}>{children}</FeedCostsContext.Provider>;
}

// Asserts the provider, and that assertion is what gives every consumer a
// non-null type. Not a new failure mode: the hook used to return `null` outside
// its provider and every consumer destructures the result immediately, so the
// app ALREADY crashed at that exact line with an anonymous
// `TypeError: Cannot destructure property … of null`. Same failure, same point,
// named cause.
export function useFeedCosts(): FeedCostsContextValue {
  const value = useContext(FeedCostsContext);
  if (!value) throw new Error('useFeedCosts must be used within FeedCostsProvider');
  return value;
}
